import { useCallback, useEffect, useState } from "react";
import { LotterySessionDto } from "../models/Lottery";
import { useConfig } from "./useConfig";
import { useSnackbar } from "notistack";

export const useLottery = (id: string | undefined) => {
  const [lottery, setLottery] = useState<LotterySessionDto | null>(null);
  const [loading, setLoading] = useState(false);
  const { config } = useConfig();
  const { enqueueSnackbar } = useSnackbar();

  const fetchSession = useCallback(async () => {
    try {
      if (!id) return;
      setLoading(true);
      const res = await fetch(`${config.apiUrl}/lottery/session/${id}`, {
        method: "GET",
      });

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const json = (await res.json()) as LotterySessionDto;
      setLottery(json);
    } catch (err) {
      enqueueSnackbar(`${err}`, {
        variant: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [config.apiUrl, enqueueSnackbar, id]);

  const addLottery = useCallback(
    async (request: LotterySessionDto) => {
      try {
        setLoading(true);

        const res = await fetch(`${config.apiUrl}/lottery/session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        });

        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        enqueueSnackbar(`Η λοταρία αποθηκεύτηκε επιτυχώς!`, {
          variant: "success",
          autoHideDuration: 4000,
        });
        const json = (await res.json()) as LotterySessionDto;
        setLottery(json);
        return json;
      } catch (err) {
        enqueueSnackbar(`${err}`, {
          variant: "error",
          autoHideDuration: 4000,
        });
      } finally {
        setLoading(false);
      }
    },
    [config.apiUrl, enqueueSnackbar]
  );

  const updateLottery = useCallback(
    async (request: LotterySessionDto) => {
      try {
        setLoading(true);

        const res = await fetch(`${config.apiUrl}/lottery/session`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        });

        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        enqueueSnackbar(`Η λοταρία ενημερώθηκε επιτυχώς!`, {
          variant: "success",
          autoHideDuration: 4000,
        });
        const json = (await res.json()) as LotterySessionDto;
        setLottery(json);
        return json;
      } catch (err) {
        enqueueSnackbar(`${err}`, {
          variant: "error",
          autoHideDuration: 4000,
        });
      } finally {
        setLoading(false);
      }
    },
    [config.apiUrl, enqueueSnackbar]
  );

  const resetLottery = useCallback(
    async (id: string) => {
      try {
        setLoading(true);

        const res = await fetch(
          `${config.apiUrl}/lottery/session/${id}/resetDraw`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        enqueueSnackbar(`Η λοταρία αρχικοποιήθηκε  επιτυχώς!`, {
          variant: "success",
          autoHideDuration: 4000,
        });
        const json = (await res.json()) as LotterySessionDto;
        setLottery(json);
        return json;
      } catch (err) {
        enqueueSnackbar(`${err}`, {
          variant: "error",
          autoHideDuration: 4000,
        });
      } finally {
        setLoading(false);
      }
    },
    [config.apiUrl, enqueueSnackbar]
  );

  const deleteLottery = useCallback(
    async (sessionId: string) => {
      try {
        setLoading(true);

        const res = await fetch(
          `${config.apiUrl}/lottery/session/${sessionId}`,
          {
            method: "DELETE",
          }
        );

        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }

        setLottery(null);
      } catch (err) {
        enqueueSnackbar(`${err}`, {
          variant: "error",
          autoHideDuration: 4000,
        });
      } finally {
        setLoading(false);
      }
    },
    [config.apiUrl, enqueueSnackbar]
  );

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  return {
    lottery,
    addLottery,
    updateLottery,
    deleteLottery,
    fetchSession,
    resetLottery,
    loading,
  };
};
